/**
 * Fetches gallery images (same as Profile gallery photo tab) and returns
 * a list of slots up to the plan limit, with gray placeholders for missing slots.
 */

import { useEffect, useState } from "react";
import { ArtistService } from "@/services/artistService";
import type { GalleryImageItem } from "@/services/artistService";

export const GALLERY_LIMITS = {
  free: 2,
  economy: 4,
  premium: 8,
  elite: 12,
} as const;

export type GalleryPlan = keyof typeof GALLERY_LIMITS;

export type GallerySlot =
  | { type: "image"; id: number; url: string }
  | { type: "placeholder"; index: number };

export function useGalleryImagesForPreview(plan: GalleryPlan) {
  const [images, setImages] = useState<GalleryImageItem[]>([]);
  const [loading, setLoading] = useState(true);

  const limit = GALLERY_LIMITS[plan];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ArtistService.getGalleryImages()
      .then((data) => {
        if (!cancelled) setImages(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setImages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const slots: GallerySlot[] = [];
  for (let i = 0; i < limit; i++) {
    const img = images[i];
    if (img?.url) {
      slots.push({ type: "image", id: img.id, url: img.url });
    } else {
      slots.push({ type: "placeholder", index: i });
    }
  }

  return {
    slots,
    images,
    loading,
    limit,
  };
}
